import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from './supabase';
import type { DashboardRow, Practice, PracticeMember, PracticeMemberRole, WaitlistRow } from './types';

// ---------- DASHBOARD ----------
export function useDashboard() {
  return useQuery({
    queryKey: ['dashboard'],
    queryFn: async (): Promise<DashboardRow[]> => {
      const { data, error } = await supabase
        .from('admin_waitlist_dashboard')
        .select('*');
      if (error) throw error;
      return (data ?? []) as DashboardRow[];
    },
  });
}

// ---------- WAITLIST ----------
export interface WaitlistFilters {
  product?: string;
  status?: 'pending' | 'notified' | 'confirmed' | 'unsubscribed';
  persona?: string;
  search?: string; // email/name substring
  referrer_slug?: string;
}

export function useWaitlist(filters: WaitlistFilters) {
  return useQuery({
    queryKey: ['waitlist', filters],
    queryFn: async (): Promise<WaitlistRow[]> => {
      let q = supabase
        .from('admin_waitlist_full')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);

      if (filters.product) q = q.eq('product', filters.product);
      if (filters.status) q = q.eq('status', filters.status);
      if (filters.persona) q = q.eq('persona', filters.persona);
      if (filters.referrer_slug)
        q = q.eq('referrer_slug', filters.referrer_slug);
      if (filters.search) {
        // email OR name (case-insensitive contains)
        const s = filters.search.replace(/[%_]/g, '\\$&');
        q = q.or(`email.ilike.%${s}%,name.ilike.%${s}%`);
      }

      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as WaitlistRow[];
    },
  });
}

export function useMarkSignup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      action: 'notify' | 'unmark_notify' | 'confirm' | 'unconfirm' | 'unsubscribe' | 'reactivate';
      adminNotes?: string;
    }) => {
      const patch: Record<string, unknown> = {};
      switch (input.action) {
        case 'notify':         patch.notified_at = new Date().toISOString(); break;
        case 'unmark_notify':  patch.notified_at = null; break;
        case 'confirm':        patch.confirmed_at = new Date().toISOString(); break;
        case 'unconfirm':      patch.confirmed_at = null; break;
        case 'unsubscribe':    patch.unsubscribed_at = new Date().toISOString(); break;
        case 'reactivate':     patch.unsubscribed_at = null; break;
      }
      if (input.adminNotes !== undefined) patch.admin_notes = input.adminNotes;

      const { error } = await supabase
        .from('waitlist_signups')
        .update(patch)
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['waitlist'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

// ---------- PRACTICES ----------
export function usePractices() {
  return useQuery({
    queryKey: ['practices'],
    queryFn: async (): Promise<Practice[]> => {
      const { data, error } = await supabase
        .from('practices')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as Practice[];
    },
  });
}

export function usePractice(slug: string | undefined) {
  return useQuery({
    queryKey: ['practice', slug],
    enabled: !!slug,
    queryFn: async (): Promise<Practice | null> => {
      if (!slug) return null;
      const { data, error } = await supabase
        .from('practices')
        .select('*')
        .eq('slug', slug)
        .maybeSingle();
      if (error) throw error;
      return data as Practice | null;
    },
  });
}

export function useSavePractice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<Practice> & { slug: string }) => {
      const { id, created_at, updated_at, ...rest } = input;
      void created_at;
      void updated_at;

      if (id) {
        const { error } = await supabase
          .from('practices')
          .update(rest)
          .eq('id', id);
        if (error) throw error;
        return id;
      } else {
        const { data, error } = await supabase
          .from('practices')
          .insert([rest])
          .select('id')
          .single();
        if (error) throw error;
        return data.id as string;
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['practices'] });
      void qc.invalidateQueries({ queryKey: ['practice'] });
    },
  });
}

export function useTogglePracticeActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; active: boolean }) => {
      const { error } = await supabase
        .from('practices')
        .update({ active: input.active })
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['practices'] });
    },
  });
}

// ---------- PRACTICE MEMBERS (painel pediatra) ----------
export function usePracticeMembers(practiceId: string | undefined) {
  return useQuery({
    queryKey: ['practice-members', practiceId],
    enabled: !!practiceId,
    queryFn: async (): Promise<PracticeMember[]> => {
      const { data, error } = await supabase.rpc('admin_list_practice_members', {
        p_practice_id: practiceId,
      });
      if (error) throw error;
      return (data ?? []) as PracticeMember[];
    },
  });
}

export function useLinkPracticeMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      practiceId: string;
      email: string;
      role: PracticeMemberRole;
    }) => {
      const { data, error } = await supabase.rpc('admin_link_practice_member', {
        p_practice_id: input.practiceId,
        p_email: input.email.trim().toLowerCase(),
        p_role: input.role,
      });
      if (error) throw error;
      return data as PracticeMember;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['practice-members', vars.practiceId] });
    },
  });
}

export function useUnlinkPracticeMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { memberId: string; practiceId: string }) => {
      const { data, error } = await supabase.rpc('admin_unlink_practice_member', {
        p_member_id: input.memberId,
      });
      if (error) throw error;
      if (!data) throw new Error('Membro não encontrado');
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['practice-members', vars.practiceId] });
    },
  });
}
